import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuthStore } from "@/lib/api-functions/api-auth";
import apiService from "@/lib/api-functions/api-service";
import { updateLastOnline } from "@/lib/utils/lastOnlineTracker";
import { usePlatformTranslation } from "@/hooks/usePlatformTranslation";
import CustomCheckbox from "@/components/ui/CustomCheckbox";
import AuthLayout from "./AuthLayout";
import { useAuthTokenHandler } from "./useAuthTokenHandler";

export default function LoginPage() {
  const { t } = usePlatformTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [confirmationMessage, setConfirmationMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const { signIn } = useAuthStore();

  // Handle email confirmation tokens
  useAuthTokenHandler({ setLoading, setError, setConfirmationMessage });

  // Load remembered email on mount
  useEffect(() => {
    const rememberedEmail = localStorage.getItem("rememberedEmail");
    if (rememberedEmail) {
      setEmail(rememberedEmail);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setConfirmationMessage("");

    try {
      console.log("Attempting login with email:", email);
      // Pass rememberMe to signIn for token persistence
      const result = await signIn(email, password, rememberMe);

      if (result.error) {
        console.error("Login failed:", result.error);
        setError(result.error);
        setLoading(false);
        return;
      }

      if (rememberMe) {
        localStorage.setItem("rememberedEmail", email);
      } else {
        localStorage.removeItem("rememberedEmail");
      }

      // Check if user has completed their profile (lightweight check — no full data load)
      try {
        const userContextResponse = await apiService.fetchUserContextSummary();

        if (!userContextResponse.success || !userContextResponse.data) {
          setLoading(false);
          navigate("/finish-profile", { replace: true });
          return;
        }

        const { currentUser, companies } = userContextResponse.data;

        if (!currentUser?.full_name) {
          setLoading(false);
          navigate("/finish-profile", { replace: true });
          return;
        }

        const hasCompanies = companies && companies.length > 0;
        if (!hasCompanies) {
          setLoading(false);
          navigate("/finish-profile", { replace: true });
          return;
        }

        // Update Last_Online timestamp on successful login
        updateLastOnline(true).catch(err => {
          console.error('Failed to update Last_Online on login:', err);
        });

        setLoading(false);
        navigate("/home", { replace: true });

      } catch {
        setLoading(false);
        navigate("/finish-profile", { replace: true });
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : "Unexpected error";
      console.error("Login error:", err);
      setError(errorMessage);
      setLoading(false);
    }
  };

  return (
    <AuthLayout>
      <h2 className="text-2xl font-bold text-center text-gray-900 mb-8">
        Welcome back
      </h2>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Email Input */}
        <div>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('platform.pages.login.email_address')}
            required
            className="w-full h-8 px-4 py-3 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
        </div>

        {/* Password Input */}
        <div className="relative">
          <input
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t('platform.common.password')}
            required
            className="w-full h-8 px-4 py-3 pr-10 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
          >
            {showPassword ? (
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            )}
          </button>
        </div>

        {/* Remember Me Checkbox */}
        <CustomCheckbox
          id="rememberMe"
          checked={rememberMe}
          onChange={setRememberMe}
          label={t('platform.pages.login.keep_me_logged_in')}
          className="mb-4"
          size="sm"
        />

        {/* Submit Button */}
        <button
          type="submit"
          disabled={loading}
          className="w-full h-8 bg-brand-brand_purple text-white font-medium rounded-sm hover:bg-brand-brand_purple focus:outline-none focus:ring-2 focus:ring-brand-brand_purple focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {loading ? "Logging in..." : "Login"}
        </button>

        {/* Confirmation Message */}
        {confirmationMessage && (
          <div className="text-green-600 text-sm text-center bg-green-50 p-3 rounded-sm border border-green-200">
            {confirmationMessage}
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="text-red-600 text-sm text-center bg-red-50 p-3 rounded-sm border border-red-200">
            {error}
          </div>
        )}

        {/* Links */}
        <div className="flex justify-center text-sm">
          <Link
            to="/signup"
            className="text-brand-brand_purple hover:text-purple-700 font-medium"
          >
            Sign up
          </Link>
        </div>
      </form>
    </AuthLayout>
  );
}

